import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { MessageCircle, Zap, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

function UpsellPage() {
  const navigate = useNavigate();
  const [whatsappSelected, setWhatsappSelected] = useState(false);
  const [expressSelected, setExpressSelected] = useState(false);

  const storedBasePrice = parseFloat(sessionStorage.getItem('basePrice') || '49');
  const discountCode = sessionStorage.getItem('discount_code') || '';
  const discountPct = parseInt(sessionStorage.getItem('discount_pct') || '0', 10);

  const basePrice = storedBasePrice;
  const whatsappPrice = 29;
  const expressPrice = 19;

  const calculateTotal = () => {
    let total = basePrice;
    if (whatsappSelected) total += whatsappPrice;
    if (expressSelected) total += expressPrice;
    return Math.round(total * 100) / 100;
  };

  const handleProceed = () => {
    // Store selections in sessionStorage
    sessionStorage.setItem('whatsappUpsell', whatsappSelected);
    sessionStorage.setItem('expressUpsell', expressSelected);
    sessionStorage.setItem('totalPrice', calculateTotal());
    
    // Navigate to payment with pricing info
    navigate('/payment', { 
      state: { 
        basePrice,
        whatsappUpsell: whatsappSelected,
        expressUpsell: expressSelected,
        totalPrice: calculateTotal()
      } 
    });
  };

  const handleSkip = () => {
    sessionStorage.setItem('whatsappUpsell', 'false');
    sessionStorage.setItem('expressUpsell', 'false');
    sessionStorage.setItem('totalPrice', basePrice);
    navigate('/payment', { 
      state: { 
        basePrice,
        whatsappUpsell: false,
        expressUpsell: false,
        totalPrice: basePrice
      } 
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="py-8 sm:py-12">
        <div className="container-narrow max-w-4xl">
          {/* Header */}
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-accent text-white">Offres exclusives</Badge>
            <h1 className="text-2xl sm:text-3xl font-semibold text-foreground mb-3" data-testid="upsell-title">
              Améliorez votre dossier
            </h1>
            <p className="text-muted-foreground">
              Deux options pour maximiser vos chances de succès
            </p>
          </div>

          {/* Upsell 1 - Messagerie Instantanée */}
          <Card 
            className={`p-6 mb-6 border-2 transition-all cursor-pointer ${
              whatsappSelected ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'
            }`}
            onClick={() => setWhatsappSelected(!whatsappSelected)}
            data-testid="messaging-upsell-card"
          >
            <div className="flex items-start gap-4">
              <Checkbox 
                checked={whatsappSelected}
                onCheckedChange={setWhatsappSelected}
                className="mt-1"
                data-testid="messaging-checkbox"
              />
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="inline-flex items-center justify-center w-10 sm:w-12 h-10 sm:h-12 rounded-full bg-blue-100 flex-shrink-0">
                      <MessageCircle className="w-5 sm:w-6 h-5 sm:h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base sm:text-lg text-foreground">Messagerie avec votre conseiller</h3>
                      <Badge variant="outline" className="text-xs mt-1">Recommandé</Badge>
                    </div>
                  </div>
                  <div className="sm:text-right flex-shrink-0 ml-13 sm:ml-0">
                    <div className="text-xl sm:text-2xl font-bold text-accent">+CHF 29</div>
                    <p className="text-xs text-muted-foreground">TTC</p>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="space-y-3 mb-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm text-foreground">Échange direct via messagerie sécurisée</p>
                      <p className="text-xs text-muted-foreground">
                        Discutez avec votre conseiller directement dans votre espace client
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm text-foreground">Compréhension complète de votre problématique</p>
                      <p className="text-xs text-muted-foreground">
                        Votre conseiller s'assure qu'aucun élément important n'est oublié
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm text-foreground">Réduction des erreurs</p>
                      <p className="text-xs text-muted-foreground">
                        Un courrier plus précis = plus de chances d'obtenir gain de cause
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm text-foreground">Accompagnement personnalisé et rassurant</p>
                      <p className="text-xs text-muted-foreground">
                        Vous n'êtes pas seul face aux administrations
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-900 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <strong>Important :</strong> Les dossiers complexes (tribunal, recours, pénal) bénéficient 
                    fortement de cet accompagnement personnalisé.
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Upsell 2 - Express Delivery */}
          <Card 
            className={`p-6 mb-8 border-2 transition-all cursor-pointer ${
              expressSelected ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'
            }`}
            onClick={() => setExpressSelected(!expressSelected)}
            data-testid="express-upsell-card"
          >
            <div className="flex items-start gap-4">
              <Checkbox 
                checked={expressSelected}
                onCheckedChange={setExpressSelected}
                className="mt-1"
                data-testid="express-checkbox"
              />
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="inline-flex items-center justify-center w-10 sm:w-12 h-10 sm:h-12 rounded-full bg-amber-100 flex-shrink-0">
                      <Zap className="w-5 sm:w-6 h-5 sm:h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base sm:text-lg text-foreground">Livraison Express</h3>
                      <Badge variant="outline" className="text-xs mt-1">Traitement prioritaire</Badge>
                    </div>
                  </div>
                  <div className="sm:text-right flex-shrink-0 ml-13 sm:ml-0">
                    <div className="text-xl sm:text-2xl font-bold text-accent">+CHF 19</div>
                    <p className="text-xs text-muted-foreground">TTC</p>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="space-y-3 mb-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm text-foreground">Livraison sous 24h</p>
                      <p className="text-xs text-muted-foreground">
                        Votre courrier traité en priorité et livré en 24h au lieu de 48h
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm text-foreground">Respect des délais administratifs</p>
                      <p className="text-xs text-muted-foreground">
                        Ne prenez aucun risque si vous approchez de la date limite de 30 jours
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm text-foreground">Tranquillité d'esprit</p>
                      <p className="text-xs text-muted-foreground">
                        Répondez rapidement aux autorités sans stress
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-900 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <strong>Attention :</strong> Si vous avez reçu la décision il y a plus de 2 semaines, 
                    la livraison express est fortement recommandée.
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Récapitulatif prix */}
          <Card className="p-6 mb-6 bg-primary/5 border-primary/20">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Service de base</span>
                <span className={`font-medium ${discountPct > 0 ? 'line-through text-muted-foreground' : ''}`}>CHF 49</span>
              </div>
              {discountPct > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-green-700">Réduction ({discountPct}%)</span>
                  <span className="font-medium text-green-700">CHF {basePrice.toFixed(2)}</span>
                </div>
              )}
              {whatsappSelected && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Messagerie instantanée</span>
                  <span className="font-medium text-accent">+CHF 29</span>
                </div>
              )}
              {expressSelected && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Livraison Express</span>
                  <span className="font-medium text-accent">+CHF 19</span>
                </div>
              )}
              <Separator className="my-3" />
              <div className="flex justify-between text-lg font-semibold">
                <span>Total</span>
                <span className="text-accent" data-testid="total-price">CHF {calculateTotal()}</span>
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              size="lg"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 sm:h-14 text-sm sm:text-lg font-semibold"
              onClick={handleProceed}
              data-testid="proceed-payment-button"
            >
              Continuer — CHF {calculateTotal()}
            </Button>

            <Button
              size="lg"
              variant="ghost"
              className="w-full h-10 sm:h-12 text-muted-foreground hover:text-foreground text-xs sm:text-sm"
              onClick={handleSkip}
              data-testid="skip-upsells-button"
            >
              Non merci, continuer avec CHF 49
            </Button>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Paiement sécurisé · Sans engagement · Satisfaction garantie
          </p>
        </div>
      </div>
    </div>
  );
}

export default UpsellPage;
